import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Headphones, GraduationCap } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

const SoulversityHero: React.FC = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const isMobile = useIsMobile();

  const sliderImages = [
    "/lovable-uploads/35d6ff96-c06b-4787-84bc-64318cfa9fb0.png",
    "/lovable-uploads/a82b0776-f1d8-4ac8-b107-f1ce533cdd9a.png",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [sliderImages.length]);

  const scrollToLearn = () => {
    const el = document.getElementById('learn');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const serviceCards = [
    {
      title: "Speak Your Heart",
      description: "We listen mindfully. No judgement.",
      icon: <MessageSquare className="h-6 w-6" />,
      action: () => navigate("/services/mindful-listening"),
      color: "from-ifind-teal/70 to-ifind-teal/90",
    },
    {
      title: "Get Live Support",
      description: "1-on-1 guidance from live experts, on-call now.",
      icon: <Headphones className="h-6 w-6" />,
      action: () => navigate("/experts"),
      color: "from-ifind-purple/70 to-ifind-purple/90",
    },
    {
      title: "Learn at Soulversity",
      description: "Meditation teacher training & transformative courses.",
      icon: <GraduationCap className="h-6 w-6" />,
      action: scrollToLearn,
      color: "from-ifind-aqua/70 to-ifind-aqua/90",
    },
  ];

  const ServiceCard = ({ title, description, icon, action, color }: any) => (
    <div
      onClick={action}
      className={`cursor-pointer rounded-lg bg-gradient-to-r ${color} backdrop-blur-sm text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 flex flex-col`}
    >
      <div className="p-4 md:p-5 flex flex-col h-full">
        <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
          {icon}
          <h3 className="text-base md:text-lg lg:text-xl font-bold">{title}</h3>
        </div>
        <p className="text-xs md:text-sm lg:text-base opacity-90">{description}</p>
      </div>
    </div>
  );

  return (
    <div className="relative h-screen w-full">
      <div className="relative w-full h-full overflow-hidden">
        {sliderImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${
              currentSlide === index ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={image}
              alt={`Soulversity Hero Slide ${index + 1}`}
              className="w-full h-full object-cover"
              style={{ objectPosition: 'center 30%' }}
              loading={index === 0 ? "eager" : "lazy"}
            />
            <div className="absolute inset-0 bg-black/20" />
          </div>
        ))}

        <div
          className="absolute inset-0 flex flex-col justify-start items-start"
          style={{ paddingTop: isMobile ? '160px' : '180px', paddingLeft: isMobile ? '24px' : '60px' }}
        >
          <p
            className="text-white/90 font-medium mb-3"
            style={{ fontSize: isMobile ? '18px' : '22px', letterSpacing: '0.05em' }}
          >
            One Platform.
          </p>
          <h1
            className="text-white font-bold text-left"
            style={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: isMobile ? '48px' : '88px',
              lineHeight: isMobile ? '54px' : '96px',
              fontWeight: 700,
              maxWidth: '720px',
            }}
          >
            Get Support.<br />
            Learn. Transform.
          </h1>
          <p
            className="text-white/90 mt-4 max-w-xl"
            style={{ fontSize: isMobile ? '14px' : '18px' }}
          >
            iFindLife meets Soulversity — compassionate live support and world-class meditation training, in one place.
          </p>
        </div>

        <div className="absolute bottom-8 sm:bottom-10 md:bottom-12 left-0 right-0 px-4 sm:px-6 md:px-12 lg:px-[60px]">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-5">
            {serviceCards.map((card, index) => (
              <ServiceCard key={index} {...card} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SoulversityHero;
